
import * as clock from "../util/clock.js";
import * as d3 from "../lib/d3.js";
import * as utc from "../util/utc.js";
import {$} from "../util/seq.js";
import {clamp, distance, rescale} from "../util/math.js";
import {loadEpak} from "../network/fetcher.js";
import {report} from "../report.js";
import {debounce, isEqual, sortedIndex} from "../lib/underscore.js";
import {τ, HOUR} from "../util/consts.js";
import {createUnitDescriptors} from "../util/units.js";
import {gaiaPath} from "../product/gaia.js";
import {tr} from "../ux/translations.js";
import * as sources from "../product/sources.js";


function download(...files) {
    return Promise.allSettled(files.map(e => gaiaPath("data/firms", e)).map(loadEpak)).then(results => {
        // skip files we couldn't download
        return results.filter(e => e.status === "fulfilled").map(e => e.value);
    });
}

let product;  // small little cache to avoid unnecessary downloads

function buildProduct(time_cursor) {
    const dir = time_cursor === "current" ? "current" : utc.print(time_cursor, "{YYYY}");
    const stamp = time_cursor === "current" ? "current" : utc.print(time_cursor, "{YYYY}{MM}{DD}");
    const files = [`${dir}/${stamp}-firms.epak`];
    if (time_cursor !== "current") {
        const prevDay = utc.normalize(utc.add(time_cursor, {day: -1}));
        const path = `${utc.print(prevDay, "{YYYY}")}/${utc.print(prevDay, "{YYYY}{MM}{DD}")}-firms.epak`;
        files.push(path);
    }
    if (isEqual(files, product?.files)) {
        // UNDONE caching??? some of these files will eventually be stale... maybe keep the memo for just N minutes?
        return Promise.resolve(product);
    }
    return download(...files).then(processFires).then(payload => {
        return product = {files, payload};
    });
}

function convert(epak) {
    const [timestamp, lon, lat, frp] = epak.blocks;
    const results = new Array(timestamp.length);
    for (let i = 0; i < timestamp.length; i++) {
        results[i] = {timestamp: timestamp[i], coords: [lon[i], lat[i]], frp: frp[i]};
    }
    return results;
}

function processFires(fires) {
    const by_power_desc = (a, b) => b.frp - a.frp;
    const by_time_then_power_asc = (a, b) => a.timestamp - b.timestamp || a.frp - b.frp;

    const detections = fires.map(convert).flat().sort(by_time_then_power_asc)/*.filter(e => e.frp >= 1)*/;
    const index = detections.map(e => e.timestamp);

    return time_cursor => {
        const timestamp = time_cursor === "current" ? clock.now() : +utc.date(time_cursor);
        const start = sortedIndex(index, timestamp - 16*HOUR);
        const end = sortedIndex(index, timestamp + 1);

        const allFires = detections.slice(start, end).map(e => {
            const age = timestamp - e.timestamp;
            const greyFactor = clamp(rescale(age, 4*HOUR, 11*HOUR, 1, 0.5), 0.5, 1);
            const fade = clamp(rescale(age, 8*HOUR, 16*HOUR, 1, 0), 0, 1);
            const c = d3.hsl(d3.interpolateYlOrRd(clamp(rescale(e.frp, 0, 50, 1, 0.4), 0.4, 1)));
            c.s *= greyFactor;
            c.opacity = fade;
            return {
                timestamp: e.timestamp,
                coords: e.coords,
                frp: e.frp,
                radius: clamp(rescale(e.frp, 0, 50, 0.75, 1), 0.75, 1),
                color: c.formatRgb(),
            };
        });

        // find the top fires in the subset
        const topFires = Array.from(allFires).sort(by_power_desc).slice(0, 2000).sort(by_time_then_power_asc);

        return {
            timestamp,
            topFires,
            allFires,
        };
    };
}

function findClosest(circles, spotlight_point) {
    // find the first circle (in descending z-order) where the spotlight point is located inside its boundary
    if (spotlight_point !== undefined) {
        for (let i = circles.length - 1; i >= 0; i--) {
            const circle = circles[i];
            const [,,r] = circle;
            if (distance(circle, spotlight_point) <= r) {
                return circle;
            }
        }
    }
}

function newSceneBuilder(viewboxAgent, sharedStore) {

    function buildScene(proj, fires) {
        const {width, height} = viewboxAgent.value();
        const boost = clamp(proj.scale() / 1000, 2, 5);
        const project = proj.optimize().project;
        const spotlight_coords = sharedStore.get("spotlight_coords") ?? [NaN, NaN];
        const [sx, sy] = project(spotlight_coords[0], spotlight_coords[1]);
        const spotlight_point = (0 <= sx && sx < width && 0 <= sy && sy < height) ? [sx, sy] : undefined;

        const circles = [];
        const occlusion = new Set();
        for (let i = fires.length - 1; i >= 0; i--) {
            const fire = fires[i];
            const {radius, coords: [lon, lat]} = fire;
            const [x, y] = project(lon, lat);
            if (0 <= x && x < width && 0 <= y && y < height) {
                // skip shadowed circles (i.e., those that fall onto the same 1/2 pixel location)
                const key = Math.round(y * 2) * 10000000 + Math.round(x * 2);
                if (!occlusion.has(key)) {
                    occlusion.add(key);
                    const r = radius * boost;
                    const circle = [x, y, r, fire];
                    circles.push(circle);
                }
            }
        }
        const closest = findClosest(circles, spotlight_point);
        return {circles, closest};
    }

    return buildScene;
}

let closest;  // the most recently rendered circle that is closest to the spotlight position

function makeFireRenderer(getFires, buildScene) {
    return {
        /**
         * @param {CanvasRenderingContext2D} context
         * @param path D3 path function
         */
        renderTo(context, path) {
            const fires = getFires();
            if (fires === undefined || fires.length === 0) {
                return;
            }

            const scene = buildScene(path.projection(), fires);
            const circles = scene.circles;
            closest = scene.closest;

            for (let i = circles.length - 1; i >= 0; i--) {
                const [x, y, r, fire] = circles[i];
                context.beginPath();
                context.fillStyle = fire.color;
                context.arc(x, y, r, 0, τ);
                context.fill();
            }

            // find the fire closest to the spotlight, if any, and render it in white
            // CONSIDER: do not draw the closest fire in this renderer but instead manage a separate svg element
            if (closest !== undefined) {
                const [x, y, r] = closest;
                context.beginPath();
                context.fillStyle = "white";
                context.arc(x, y, r, 0, τ);
                context.fill();
            }
        },
    };
}

export function defineFIRMSExtension(agents, sharedStore, sessionStore) {
    const {rendererAgent, viewboxAgent, annotationLayerAgent} = agents;

    let state;

    function attachRenderers({dynamicItemsLo, dynamicItemsHi} = {}) {
        if (!dynamicItemsLo || !dynamicItemsHi) {
            return;
        }
        const buildScene = newSceneBuilder(viewboxAgent, sharedStore);
        dynamicItemsLo.add(makeFireRenderer(() => state?.topFires, buildScene));
        dynamicItemsHi.add(makeFireRenderer(() => state?.allFires, buildScene));
        rendererAgent.value()?.draw();
    }

    function newLayer() {

        function findClosest(/*λ, φ*/) {
            // re-render to find the closest but also to highlight it
            rendererAgent.value()?.draw();
            return closest;
        }

        function format(closest) {
            const fire = closest?.[3];
            if (fire === undefined) {
                return;
            }
            const isDeviceTZ = sessionStore.get("display_timezone") === "device";
            const parts = isDeviceTZ ? utc.localParts(fire.timestamp) : utc.parts(fire.timestamp);
            return {
                formattedVal: `${utc.print(parts, "{MM}-{DD} {hh}:{mm}")},\u00a0\u00a0 ${fire.frp.toFixed(2)}`,
                fullText: "UNDONE",
            };
        }

        return {
            type: "fires",
            sourceHTML: sources.firms,
            descriptionHTML: {
                name: tr`Fire Detections`,
                qualifier: "",
            },
            field: () => ({"bilinear": findClosest, "nearest": findClosest}),
            unitDescriptors: createUnitDescriptors({
                "MW": {convert: x => x, format},
            }),
        };
    }

    function updateStateAndRender() {
        const {time_cursor, annotation} = sharedStore.getAll();
        if (annotation !== "fires") {
            state = undefined;
            rendererAgent.value()?.draw();
            annotationLayerAgent.submit(undefined);
            return;
        }

        buildProduct(time_cursor).then(product => {
            state = product.payload(time_cursor);
            rendererAgent.value()?.draw();
            annotationLayerAgent.submit(newLayer());
        }).catch(report.error);
    }

    rendererAgent.on($`update`, attachRenderers);

    const updateStateAndRender_debounced = debounce(updateStateAndRender);

    sharedStore.on($`change:time_cursor`, updateStateAndRender_debounced);
    sharedStore.on($`change:annotation`, updateStateAndRender_debounced);
    updateStateAndRender_debounced();  // initialize
}
